import { useEffect, useMemo, useState, useCallback } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';

const fmtMin = (m) => {
  if (!m || m <= 0) return '';
  const h = Math.floor(m / 60); const r = m % 60;
  if (h === 0) return `${r}m`;
  if (r === 0) return `${h}h`;
  return `${h}h${r}m`;
};
const toIST = (d) => {
  const ist = new Date(d.getTime() + (5.5 * 60 - d.getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
};
const ymd = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const monthLabel = (y, m) => new Date(y, m, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });

export default function AttendancePage() {
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed
  const [data, setData] = useState({ users: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const monthStart = ymd(year, month, 1);
  const monthEnd = ymd(year, month, daysInMonth(year, month));
  const todayStr = toIST(new Date());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/attendance/team', { params: { from_date: monthStart, to_date: monthEnd } });
      setData(res.data || { users: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [monthStart, monthEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data.users;
    const s = search.toLowerCase();
    return data.users.filter(u => u.user_name?.toLowerCase().includes(s));
  }, [data, search]);

  // Pre-index sessions by user + date for quick render
  const cellMap = useMemo(() => {
    const map = new Map(); // key = `${user_id}|${date}` -> {total_minutes, is_open}
    for (const u of filtered) {
      for (const d of u.days || []) {
        map.set(`${u.user_id}|${d.date}`, d);
      }
    }
    return map;
  }, [filtered]);

  const days = useMemo(() => {
    const n = daysInMonth(year, month);
    return Array.from({ length: n }, (_, i) => ({
      day: i + 1,
      iso: ymd(year, month, i + 1),
      dayOfWeek: new Date(year, month, i + 1).getDay(),
    }));
  }, [year, month]);

  const exportCsv = () => {
    if (!filtered.length) return;
    const head = ['Employee', 'Role', ...days.map(d => d.iso), 'Total (h:m)'];
    const lines = [head.join(',')];
    for (const u of filtered) {
      const cells = days.map(d => {
        const c = cellMap.get(`${u.user_id}|${d.iso}`);
        return c ? fmtMin(c.total_minutes) : '';
      });
      const totalH = Math.floor(u.total_minutes / 60);
      const totalM = u.total_minutes % 60;
      lines.push([u.user_name, u.role, ...cells, `${totalH}h${totalM}m`].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `attendance_${year}-${String(month + 1).padStart(2, '0')}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); } else { setMonth(m => m - 1); }
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); } else { setMonth(m => m + 1); }
  };

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="attendance-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="attendance-heading">
            <CalendarDays className="h-7 w-7 text-[#5B6FD6]" /> Attendance
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Monthly punch-in/out grid. Each cell shows total worked time (sum of all sessions that day). Empty cells = no punch-in recorded.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length} data-testid="attendance-export-btn">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      <Card className="border-slate-200 p-3 bg-white">
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="attendance-month-bar">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={prevMonth} data-testid="attendance-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="text-base md:text-lg font-semibold text-slate-800 min-w-[160px] text-center" data-testid="attendance-month-label">
              {monthLabel(year, month)}
            </div>
            <Button variant="outline" size="icon" onClick={nextMonth} data-testid="attendance-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }}>This month</Button>
          </div>
          <div className="flex-1 min-w-[200px] max-w-md">
            <Input placeholder="Search employee…" value={search} onChange={e => setSearch(e.target.value)} className="bg-white" data-testid="attendance-search" />
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-xs w-full" data-testid="attendance-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-slate-50 z-10 min-w-[180px]">Employee</th>
                {days.map(d => {
                  const isWeekend = d.dayOfWeek === 0 || d.dayOfWeek === 6;
                  const isToday = d.iso === todayStr;
                  return (
                    <th key={d.iso} className={`text-center px-1.5 py-2 font-semibold min-w-[42px] ${isWeekend ? 'bg-slate-100/60' : ''} ${isToday ? 'bg-emerald-100' : ''}`}>
                      {d.day}
                    </th>
                  );
                })}
                <th className="text-right px-3 py-2 font-semibold sticky right-0 bg-slate-50 z-10">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={days.length + 2} className="text-center text-slate-400 py-10">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={days.length + 2} className="text-center text-slate-400 py-10">No employees match.</td></tr>
              ) : filtered.map(u => {
                const totalH = Math.floor(u.total_minutes / 60);
                const totalM = u.total_minutes % 60;
                return (
                  <tr key={u.user_id} data-testid={`attendance-row-${u.user_id}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white z-10">
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 rounded-full bg-gradient-to-br from-[#5B6FD6] to-[#10B981] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                          {u.user_name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">{u.user_name}</div>
                          <div className="text-[10px] text-slate-400 capitalize">{u.role}</div>
                        </div>
                      </div>
                    </td>
                    {days.map(d => {
                      const cell = cellMap.get(`${u.user_id}|${d.iso}`);
                      const isWeekend = d.dayOfWeek === 0 || d.dayOfWeek === 6;
                      const isToday = d.iso === todayStr;
                      return (
                        <td key={d.iso} className={`text-center px-1 py-2 font-mono text-[10px] ${isWeekend ? 'bg-slate-50/60' : ''} ${isToday ? 'ring-1 ring-emerald-200' : ''}`}>
                          {cell ? (
                            <span className={`inline-block px-1 py-0.5 rounded ${cell.is_open ? 'bg-emerald-100 text-emerald-800 font-bold' : 'text-slate-700'}`}
                              title={`${cell.session_count} session${cell.session_count === 1 ? '' : 's'}${cell.first_punch_in ? ` · in ${new Date(cell.first_punch_in).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}` : ''}${cell.last_punch_out ? ` · out ${new Date(cell.last_punch_out).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}` : ''}`}
                            >
                              {fmtMin(cell.total_minutes) || (cell.is_open ? 'in' : '—')}
                            </span>
                          ) : (
                            <span className="text-slate-300">·</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="text-right px-3 py-2 sticky right-0 bg-white z-10 font-mono font-bold text-emerald-700">{totalH}h{totalM}m</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs text-slate-400">
        Tip: cells highlighted in emerald-green mean the user is still punched in today. Weekends are dimmed.
      </p>
    </div>
  );
}
